"use client";

// Result sections rendered after the API response.

import { STRENGTH_CONFIG, PLANET_ORDER, type AshtakvargaResponse } from "./AshtakvargaTypes";

function strengthBadge(strength: string) {
  const cfg = STRENGTH_CONFIG[strength as keyof typeof STRENGTH_CONFIG] ?? STRENGTH_CONFIG.average;
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-eyebrow font-semibold ${cfg.textClass} ${cfg.surfaceClass}`}
    >
      {cfg.label}
    </span>
  );
}

// ── SAV Table ────────────────────────────────────────────────────────────────

export function SAVTable({ result }: { result: AshtakvargaResponse }) {
  return (
    <section data-design-owner="ashtakvarga-sav-results">
      <h3 className="mb-3 font-serif text-subtitle-small font-bold text-text-primary">
        Sarvashtakvarga (SAV) — All 12 Signs
      </h3>
      <p className="mb-3 text-caption text-text-muted">
        Total = {result.sarvashtakvarga_table.reduce((a, b) => a + b, 0)} bindus (always 337 in
        Parashari system)
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {result.sarvashtakvarga_table.map((sav, i) => {
          const strength =
            sav >= 30
              ? "very_strong"
              : sav >= 25
                ? "average"
                : sav >= 20
                  ? "below_average"
                  : "weak";
          const cfg = STRENGTH_CONFIG[strength];
          return (
            <div
              key={i}
              className={`rounded-lg border border-border-default p-3 text-center ${cfg.surfaceClass}`}
            >
              <p className="mb-1 text-eyebrow text-text-muted">{result.sign_names[i]}</p>
              <p className={`text-title font-bold ${cfg.textClass}`}>{sav}</p>
              <p className={`mt-0.5 text-eyebrow font-semibold ${cfg.textClass}`}>{cfg.label}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── BAV Table ────────────────────────────────────────────────────────────────

export function BAVTable({ result }: { result: AshtakvargaResponse }) {
  return (
    <section data-design-owner="ashtakvarga-bav-results">
      <h3 className="mb-3 font-serif text-subtitle-small font-bold text-text-primary">
        Bhinnashtakvarga (BAV) — Per-Planet Tables
      </h3>
      <p className="mb-3 text-caption text-text-muted">
        Bindus each planet contributes to each sign (0–8). 5+ = strong, 2 or fewer = weak.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-[600px] w-full border-collapse text-caption">
          <thead>
            <tr className="border-b border-border-default">
              <th className="p-2 text-left font-semibold text-text-primary">Planet</th>
              {result.sign_names.map((sign, i) => (
                <th
                  key={i}
                  className="whitespace-nowrap p-2 text-center font-semibold text-text-secondary"
                >
                  {sign.split(" ")[0]}
                </th>
              ))}
              <th className="p-2 text-center font-semibold text-text-primary">Total</th>
            </tr>
          </thead>
          <tbody>
            {PLANET_ORDER.filter((p) => p in result.planet_ashtakvarga_tables).map((planet) => {
              const row = result.planet_ashtakvarga_tables[planet];
              const total = result.total_bindus[planet] ?? 0;
              const strength = result.planet_strength[planet] ?? "moderate";
              return (
                <tr key={planet} className="border-b border-border-default">
                  <td className="p-2 font-semibold text-text-primary">
                    {result.planet_display_en[planet] ?? planet}
                  </td>
                  {row.map((v, i) => (
                    <td
                      key={i}
                      className={`p-2 text-center ${
                        v >= 5
                          ? "font-bold text-success"
                          : v <= 2
                            ? "font-bold text-error"
                            : "text-text-secondary"
                      }`}
                    >
                      {v}
                    </td>
                  ))}
                  <td className="p-2 text-center">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-eyebrow font-bold ${
                        strength === "strong"
                          ? "bg-success/10 text-success"
                          : strength === "weak"
                            ? "bg-error/10 text-error"
                            : "bg-surface-brand-subtle text-intent-brand"
                      }`}
                    >
                      {total}
                    </span>
                  </td>
                </tr>
              );
            })}
            {/* SAV total row */}
            <tr className="bg-surface-brand-subtle text-intent-brand">
              <td className="p-2 font-bold">SAV Total</td>
              {result.sarvashtakvarga_table.map((v, i) => (
                <td key={i} className="p-2 text-center font-bold">
                  {v}
                </td>
              ))}
              <td className="p-2 text-center font-bold">337</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── House Predictions ────────────────────────────────────────────────────────

export function HousePredictions({ result }: { result: AshtakvargaResponse }) {
  return (
    <section data-design-owner="ashtakvarga-house-results">
      <h3 className="mb-3 font-serif text-subtitle-small font-bold text-text-primary">
        House-by-House Predictions
      </h3>
      <p className="mb-4 text-caption text-text-muted">
        SAV ordered by house from your lagna (ascendant).
      </p>
      <div className="space-y-3">
        {result.predictions.map((pred) => {
          const cfg =
            STRENGTH_CONFIG[pred.strength as keyof typeof STRENGTH_CONFIG] ??
            STRENGTH_CONFIG.average;
          return (
            <div
              key={pred.house}
              className={`rounded-lg border border-border-default p-4 ${cfg.surfaceClass}`}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-body-small font-semibold text-text-primary">
                  House {pred.house}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`text-body-small font-bold ${cfg.textClass}`}>{pred.sav}</span>
                  {strengthBadge(pred.strength)}
                </div>
              </div>
              <p className="text-caption leading-relaxed text-text-secondary">{pred.summary}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
